import clsx from 'clsx';
import { t } from 'i18next';
import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useAsyncEffect } from 'rooks';
import { SearchResultRemovable } from '@/core/SearchResultRemovable.molecule';
import { SelectItemSearchModal } from '@/core/SelectItemSearchModal.molecule';
import { ExplorerModalBody } from '@/explorer/ExplorerModalBody.molecule';
import * as exploreWorkbookModalActions from '@/explorer/exploreWorkbookModal.actions';
import { nodeToSelectedSearchItem } from '@/search/search.utilities';
import { randomUUID } from '@/utilities/guid.utilities';
import { getAssetFromAncestors } from '@/utilities/httpHelpers.utilities';
import { itemIconClass } from '@/utilities/utilities';
import { sqHierarchiesApi } from '@seeqdev/api/endpoint/HierarchiesApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { SeeqNames } from '@seeqdev/constants';
import { faTrash } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Alert, Icon, InputGroup, Modal } from '@seeqdev/qomponents';
import type { SelectItemSearchModalProps } from '@/core/SelectItemSearchModal.molecule';
import type { SearchModes } from '@/search/search.constants';
import type { SelectedSearchItem } from '@/search/search.types';
import type { IconType } from '@seeqdev/qomponents';

interface ModalProps {
  canSelectAll?: boolean;
  helpTextKey?: string;
}

interface OtherModalProps
  extends
    ModalProps,
    Omit<
      SelectItemSearchModalProps,
      'isMultiple' | 'body' | 'onClose' | 'onSelect' | 'footer' | 'selectAllCallback' | 'areAllSelected' | 'modalId'
    > {
  selectionType: 'Other';
}

interface AnalysisModalProps extends ModalProps {
  selectionType: 'Analysis';
  title: string;
  allowedTypes?: string[];
}

export interface SelectItemSearchProps {
  testId: string;
  value: SelectedSearchItem[] | undefined;
  onChange: (value: SelectedSearchItem[]) => void;
  notSelectedMessage: string;
  modalProps: OtherModalProps | AnalysisModalProps;
  isMultiple?: boolean;
  itemIcon?: React.ReactNode;
  getItemIcon?: (item: SelectedSearchItem) => React.ReactNode;
  showModal?: boolean;
  onModalClose?: () => void;
  extraClassNames?: string;
  extraValueClassNames?: string;
  isDisabled?: boolean;
  showAddIcon?: boolean;
  itemTypeToShowInOverview?: SearchModes;
  hideAddEditIcon?: boolean;
  hideDeleteIcon?: boolean;
  deleteIconStyle?: IconType;
  overviewLabel?: string;
  label?: string;
  canBeEdited?: boolean;
  showError?: boolean;
  restrictExploration?: boolean;
  initialItemTable?: SelectedSearchItem;
  initialHierarchy?: SelectedSearchItem;
  initialScalingTable?: SelectedSearchItem;
}

const defaultItemIcon = ({ item, testId }: { item: SelectedSearchItem; testId: string }): React.ReactNode => {
  return (
    <Icon
      icon={itemIconClass(item)}
      testId={`${testId}-icon`}
      type="inherit"
      extraClassNames="sq-fairly-dark-gray pl5 pr10"
      large={true}
    />
  );
};

const getHierarchyItem = async (
  item: SelectedSearchItem,
  t: (path: string) => string,
  hierarchy: SelectedSearchItem,
): Promise<SelectedSearchItem> => {
  const ancestors = item.ancestors ?? [];
  try {
    const columnId = item.columnId!;
    const columnValue = item.columnValue!;
    const { data: hierarchyNode } = await sqHierarchiesApi.getHierarchyNode({
      id: hierarchy.id,
      columnIds: [...ancestors.map((ancestor) => ancestor.columnId ?? ''), columnId],
      columnValues: [...ancestors.map((ancestor) => ancestor.columnValue ?? ''), columnValue],
    });
    const fullAncestors = hierarchyNode.path.map((pathNode) => nodeToSelectedSearchItem(pathNode, []));

    return nodeToSelectedSearchItem(hierarchyNode.node, fullAncestors);
  } catch (e) {
    console.error(e);

    return { name: t('SEARCH_DATA.FETCHING_ITEM_FAILED'), id: randomUUID(), properties: [] };
  }
};

const getItem = async (id: string, t: (path: string) => string): Promise<SelectedSearchItem> => {
  try {
    const { data: item } = await sqItemsApi.getItemAndAllProperties({ id });
    return { id: item.id, name: item.name, properties: item.properties, datasource: item.datasource, type: item.type };
  } catch (e) {
    console.error(e);

    return { name: t('SEARCH_DATA.FETCHING_ITEM_FAILED'), id: randomUUID(), properties: [] };
  }
};

const loadItem = async (
  item: SelectedSearchItem,
  t: (path: string) => string,
  initialHierarchy: SelectedSearchItem | undefined,
): Promise<SelectedSearchItem> => {
  if (initialHierarchy) {
    return getHierarchyItem(item, t, initialHierarchy);
  } else {
    return getItem(item.id, t);
  }
};

const formatItemName = (item: SelectedSearchItem): string => {
  return item.name ?? t('SEARCH_DATA.FETCHING_ITEM');
};

const formatItemAncestors = (item: SelectedSearchItem): string => {
  return (
    (item.ancestors?.length ? getAssetFromAncestors(item.ancestors)?.formattedName : undefined) ??
    item.datasource?.name ??
    item.properties?.find(({ name }) => name === SeeqNames.Properties.DatasourceId)?.value ??
    ''
  );
};

interface NoSelectedItemResultProps {
  testId: string;
  notSelectedMessage: string;
}

const NoSelectedItemResult = ({ testId, notSelectedMessage }: NoSelectedItemResultProps) => {
  const { t } = useTranslation();

  return (
    <div className="sq-placeholder-color" data-testid={`${testId}-notSelected`}>
      <span>{t(notSelectedMessage)}</span>
    </div>
  );
};

export const SelectItemSearch = ({
  showError,
  testId,
  value,
  onChange,
  itemIcon,
  getItemIcon,
  showModal,
  isMultiple = true,
  onModalClose,
  notSelectedMessage,
  extraClassNames,
  extraValueClassNames,
  isDisabled,
  showAddIcon,
  modalProps,
  hideAddEditIcon = false,
  hideDeleteIcon = false,
  deleteIconStyle = 'theme',
  label,
  canBeEdited = true,
  restrictExploration = false,
  initialItemTable,
  initialHierarchy,
  initialScalingTable,
}: SelectItemSearchProps) => {
  const { t } = useTranslation();

  const [isSelectingItem, setIsSelectingItem] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedSearchItem>>();
  const [selectedItemsInModal, setSelectedItemsInModal] = useState<Map<string, SelectedSearchItem>>();

  const canShowModal = (isSelectingItem || showModal) && canBeEdited;

  useEffect(() => {
    if (showModal) {
      setIsSelectingItem(true);
    }
  }, [showModal]);

  useAsyncEffect(async () => {
    if (!value?.length) {
      return;
    }

    const items = value.filter((item) => item !== undefined).map((item) => selectedItems?.get(item.id) ?? item);

    const values = await Promise.all(
      items.map<Promise<[string, SelectedSearchItem]>>(async (item) =>
        !item.name ? [item.id, await loadItem(item, t, initialHierarchy)] : [item.id, item],
      ),
    );

    const valuesMap = new Map(values);
    setSelectedItemsInModal(valuesMap);
    setSelectedItems(valuesMap);
  }, [value]);

  /** To determine whether to show "select all" or "unselect all" */
  const areAllSelected = (items: SelectedSearchItem[]) => !items?.some((item) => !selectedItemsInModal?.has(item.id));

  /** If all items are already in the list of selectedItems, unselect all, otherwise select All. */
  const selectAllCallback = (selectedSearchItems: SelectedSearchItem[]) =>
    setSelectedItemsInModal((selectedItems) => {
      const items = new Map(selectedItems);
      if (areAllSelected(selectedSearchItems)) {
        selectedSearchItems.forEach((item) => items.delete(item.id));
      } else {
        selectedSearchItems.forEach((item) => items.set(item.id, item));
      }

      return items;
    });

  const handleItemSelectInModal = (item: SelectedSearchItem) =>
    setSelectedItemsInModal((selectedItems) => {
      const itemId = item.id;
      if (isMultiple) {
        const items = new Map(selectedItems);
        if (items.has(itemId)) {
          items.delete(itemId);
        } else {
          items.set(itemId, item);
        }

        return items;
      }

      return new Map([[itemId, item]]);
    });

  const clearAndClose = () => {
    setIsSelectingItem(false);
    onModalClose?.();
  };

  const onSave = () => {
    onChange(Array.from(selectedItemsInModal?.values() ?? []));

    setSelectedItems(selectedItemsInModal);
    clearAndClose();
  };

  const onItemRemove = (itemId: string) => {
    if (!selectedItems) {
      return;
    }

    if (selectedItems.size === 1 && selectedItems.has(itemId)) {
      return onClear();
    }

    onChange(Array.from(selectedItems.values()).filter((item) => item.id !== itemId));
  };

  const onClear = () => {
    setSelectedItemsInModal(new Map());
    setSelectedItems(new Map());
    onChange([]);
  };

  const searchResult = () => {
    const item = selectedItems?.get(value?.[0]?.id ?? '');
    const ancestors = item ? formatItemAncestors(item) : undefined;

    return item ? (
      <div data-testid={`${testId}SingleResult`} className="flexColumnContainer flexFill tw:justify-between tw:py-0.5">
        <div className="flexColumnContainer tw:items-center tw:justify-center">
          {itemIcon ?? getItemIcon?.(item) ?? defaultItemIcon({ item, testId })}
        </div>
        <div className="flexFill" data-testid={`${testId}-selected`}>
          <div className="searchResultName">
            <span className="simple-word-break">{formatItemName(item)}</span>
          </div>
          {ancestors && (
            <div className="xsmall sq-fairly-dark-gray simple-word-break tw:pb-0.5 tw:italic">{ancestors}</div>
          )}
        </div>
      </div>
    ) : (
      <NoSelectedItemResult testId={testId} notSelectedMessage={notSelectedMessage} />
    );
  };

  const searchResultMultiple = () => {
    const hasItems = !!selectedItems?.size;

    return (
      <div className="flexRowContainer flexFill tw:justify-between tw:py-0.5">
        {hasItems ? (
          Array.from(selectedItems.keys()).map((itemId) => {
            const item = selectedItems.get(itemId)!;

            return (
              <SearchResultRemovable
                key={itemId}
                itemId={itemId}
                itemIcon={itemIcon ?? getItemIcon?.(item) ?? defaultItemIcon({ item, testId })}
                itemName={formatItemName(item)}
                itemAncestors={formatItemAncestors(item)}
                onRemove={onItemRemove}
              />
            );
          })
        ) : (
          <NoSelectedItemResult testId={testId} notSelectedMessage={notSelectedMessage} />
        )}
      </div>
    );
  };

  return (
    <>
      {label && (
        <div
          className="flexColumnContainer flexAlignBottom flexFill tw:justify-between tw:pb-sq-5"
          data-testid={`${testId}Label`}>
          <div className="flexColumnContainer tw:mb-sq-5 tw:items-center">
            <span className={clsx('tw:mr-sq-5')}>{t(label)}</span>
          </div>
        </div>
      )}
      <InputGroup
        showError={showError}
        field={
          <div
            className={clsx(
              'width-maximum flexColumnContainer cursorPointer multiSelectSearchResult tw:items-center tw:pl-1.5',
              { disabledInputField: isDisabled },
              extraValueClassNames,
            )}
            onClick={() => {
              if (!isDisabled && (!isMultiple || (isMultiple && !value?.length))) {
                setIsSelectingItem(true);
              }
            }}
            data-testid={`${testId}-value`}>
            {isMultiple ? searchResultMultiple() : searchResult()}
          </div>
        }
        append={[
          ((!hideDeleteIcon && selectedItems?.size) || 0) > 0
            ? {
                variant: 'button',
                buttonProps: {
                  icon: faTrash,
                  iconStyle: deleteIconStyle,
                  testId: `${testId}-delete`,
                  onClick: onClear,
                },
              }
            : undefined,
          !hideAddEditIcon
            ? {
                variant: 'button',
                buttonProps: {
                  icon: showAddIcon || isMultiple ? 'fa-plus' : 'fa-pencil',
                  iconStyle: 'theme',
                  testId: `${testId}-edit`,
                  onClick: () => !isDisabled && setIsSelectingItem(true),
                  disabled: isDisabled,
                },
              }
            : undefined,
        ]}
      />
      {canShowModal && modalProps.selectionType === 'Other' && (
        <SelectItemSearchModal
          {...modalProps}
          modalId={`${testId}-modal`}
          isMultiple={isMultiple}
          body={
            modalProps?.helpTextKey ? (
              <Alert dismissible={false} variant="theme" data-testid={`${testId}-helpText`}>
                {t(modalProps?.helpTextKey)}
              </Alert>
            ) : (
              <></>
            )
          }
          onClose={clearAndClose}
          selectItems={Array.from(selectedItemsInModal?.values() ?? [])}
          onSelect={handleItemSelectInModal}
          testId={`${testId}-modal`}
          selectAllCallback={modalProps.canSelectAll ? selectAllCallback : undefined}
          areAllSelected={areAllSelected}
          onSubmit={onSave}
          restrictExploration={restrictExploration}
          initialItemTable={initialItemTable}
          initialHierarchy={initialHierarchy}
          initialScalingTable={initialScalingTable}
        />
      )}

      {canShowModal && modalProps.selectionType === 'Analysis' && (
        <Modal
          open={true}
          onClose={clearAndClose}
          testId="selectWorkbookModal"
          title={t(modalProps.title)}
          dialogClassName="modal-md"
          onSubmit={onSave}
          disableSubmitButton={!selectedItemsInModal?.size}>
          <div className="modalBody">
            <ExplorerModalBody
              show={true}
              actions={exploreWorkbookModalActions}
              allowedTypes={modalProps.allowedTypes}
              setSelectedItem={handleItemSelectInModal}
              selectedItemIds={Array.from(selectedItemsInModal?.values() ?? []).map((item) => item.id)}
              isSelectable={true}
              selectOnlyWorkbooks={true}
              isMultiple={isMultiple}
            />
          </div>
        </Modal>
      )}
    </>
  );
};
